import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { rolePermissionAPI, tenantUsersAPI } from '../services/api';
import { getUser } from '../services/auth';
import { getSimulate, setSimulate, clearSimulate } from '../services/simulate';
import api from '../services/api';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { canUseViewAs, normalizePermissionCodes } from '../utils/permissions';
import SideNavLayout from '../components/SideNavLayout';
import './ViewAsPage.css';

/**
 * "View as role" / "View as user" page – left panel lists roles and users (expandable);
 * right panel shows current simulation and instructions. Visible to global developers or users with settings.view_as.
 */
function ViewAsPage() {
  const { tenantPath } = useTenantFromUrl();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesExpanded, setRolesExpanded] = useState(true);
  const [usersExpanded, setUsersExpanded] = useState(true);
  const [simVersion, setSimVersion] = useState(0);

  const canUse = canUseViewAs();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      rolePermissionAPI.getAllRoles(),
      tenantUsersAPI.getList().catch(() => ({ data: { data: [] } })),
    ])
      .then(([rolesRes, usersRes]) => {
        if (cancelled) return;
        setRoles(rolesRes.data?.data || []);
        setUsers(usersRes.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) {
          setRoles([]);
          setUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Re-read simulate when it may have changed (e.g. from another tab or event)
  useEffect(() => {
    const handler = () => setSimVersion((v) => v + 1);
    window.addEventListener('cfc-simulate-changed', handler);
    return () => window.removeEventListener('cfc-simulate-changed', handler);
  }, []);

  const currentSim = getSimulate();
  const simLabel = currentSim?.userName ?? currentSim?.roleName ?? '';

  const handleSelectNone = () => {
    clearSimulate();
  };

  const handleSelectRole = async (role) => {
    try {
      const res = await rolePermissionAPI.getRolePermissions(role.id);
      const permissions = (res.data?.data || []).map((p) => p.code);
      setSimulate({
        roleId: role.id,
        roleName: role.name,
        roleCode: role.code || null,
        permissions,
      });
    } catch {
      // keep current state
    }
  };

  const handleSelectUser = async (user) => {
    const displayName = user.staff_name || user.username || `User ${user.id}`;
    setSimulate({ userId: user.id, userName: displayName, permissions: [], roleCode: null });
    try {
      const whoami = await api.get('/whoami');
      const permissions = normalizePermissionCodes(whoami.data?.permissions);
      const roleCode = whoami.data?.membership?.role_code ?? null;
      setSimulate({ userId: user.id, userName: displayName, permissions, roleCode });
    } catch {
      setSimulate({ userId: user.id, userName: displayName, permissions: [], roleCode: null });
    }
  };

  if (!canUse) {
    return (
      <div className="view-as-page view-as-forbidden">
        <p>You don’t have access to View as.</p>
        <Link to={tenantPath('/dashboard')}>Back to dashboard</Link>
      </div>
    );
  }

  const nav = (
    <>
      <div className="side-nav-header">
        <h2 className="side-nav-title">View as</h2>
      </div>
      <div className="view-as-nav-body">
        <button
          type="button"
          className={`side-nav-link view-as-nav-item ${!currentSim ? 'active' : ''}`}
          onClick={handleSelectNone}
        >
          No simulation
        </button>

        <div className="view-as-nav-group">
          <button
            type="button"
            className="view-as-nav-group-title"
            onClick={() => setRolesExpanded((e) => !e)}
            aria-expanded={rolesExpanded}
          >
            <span>View as role</span>
            <span className="view-as-nav-chevron">{rolesExpanded ? '▼' : '▶'}</span>
          </button>
          {rolesExpanded && (
            <div className="view-as-nav-group-items">
              {loading ? (
                <div className="view-as-nav-loading">Loading…</div>
              ) : (
                roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    className={`side-nav-link view-as-nav-item ${currentSim?.roleId === role.id ? 'active' : ''}`}
                    onClick={() => handleSelectRole(role)}
                  >
                    {role.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="view-as-nav-group">
          <button
            type="button"
            className="view-as-nav-group-title"
            onClick={() => setUsersExpanded((e) => !e)}
            aria-expanded={usersExpanded}
          >
            <span>View as user</span>
            <span className="view-as-nav-chevron">{usersExpanded ? '▼' : '▶'}</span>
          </button>
          {usersExpanded && (
            <div className="view-as-nav-group-items">
              {loading ? (
                <div className="view-as-nav-loading">Loading…</div>
              ) : (
                users.map((user) => {
                  const displayName = user.staff_name || user.username || `User ${user.id}`;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      className={`side-nav-link view-as-nav-item ${currentSim?.userId === user.id ? 'active' : ''}`}
                      onClick={() => handleSelectUser(user)}
                    >
                      <span className="view-as-nav-user-name">{displayName}</span>
                      {user.roles?.length > 0 && (
                        <span className="view-as-nav-user-roles">
                          {user.roles.map((r) => r.role_name).join(', ')}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const content = (
    <div className="view-as-content">
      <h1 className="view-as-content-title">View as</h1>
      <p className="view-as-content-intro">
        Select a <strong>role</strong> or <strong>user</strong> in the left panel to view the app as that role or user.
        Use this to test permissions and data scoping (e.g. what a service coordinator sees).
      </p>
      {currentSim ? (
        <div className="view-as-current-box">
          <h2>Currently viewing as</h2>
          <p className="view-as-current-label">{simLabel}</p>
          <button type="button" className="view-as-end-btn" onClick={handleSelectNone}>
            End simulation
          </button>
        </div>
      ) : (
        <div className="view-as-current-box view-as-current-none">
          <p>No simulation active. You are using your own permissions.</p>
        </div>
      )}
    </div>
  );

  return (
    <SideNavLayout nav={nav} navClassName="view-as-nav">
      {content}
    </SideNavLayout>
  );
}

export default ViewAsPage;
